import { useCallback, useEffect, useState } from 'react';
import { Box, FormControlLabel, Switch, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  stringifyEntityRef,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
} from '@backstage/catalog-model';
import { kindPalette } from './CatalogGraphNode';
import {
  getDependencyColors,
  dependencyTier,
  dependencyLabel,
  readDependencyGrades,
  type DependencyGrade,
} from './dependencyCriticality';

/** Structural (partOf/hasPart) and API relations, which carry no criticality. */
function relationColor(isDark: boolean): string {
  return isDark ? 'rgba(255,255,255,0.25)' : 'rgba(66,66,66,0.35)';
}

const ARROW_PREFIX = 'catalog-graph-arrow';
const TIERS = ['critical', 'high', 'medium', 'low', 'unknown'] as const;

/** The diagram is far denser than the force graph, so it uses its own thinner scale. */
const EDGE_WIDTHS: Record<string, number> = {
  critical: 1.8,
  high: 1.5,
  medium: 1.4,
  low: 1.2,
  unknown: 1,
  relation: 1,
};

const CORNER_RADIUS = 8;

/** Keeps label text readable where it crosses an edge, without a backing box. */
const HALO_DARK = {
  paintOrder: 'stroke',
  stroke: '#2e2e2e',
  strokeWidth: 3,
  strokeLinejoin: 'round',
} as const;

const HALO_LIGHT = {
  paintOrder: 'stroke',
  stroke: '#ffffff',
  strokeWidth: 3,
  strokeLinejoin: 'round',
} as const;

type Point = { x: number; y: number };

/** Moves `radius` from `from` towards `toward`, clamped to half the segment. */
function towards(from: Point, toward: Point, radius: number): Point {
  const dx = toward.x - from.x;
  const dy = toward.y - from.y;
  const length = Math.hypot(dx, dy);
  if (!length) return { x: from.x, y: from.y };
  const t = Math.min(radius, length / 2) / length;
  return { x: from.x + dx * t, y: from.y + dy * t };
}

/**
 * Dagre emits a waypoint per rank crossed, plus a sideways one for the edge
 * label dummy node (labelPosition RIGHT / labelOffset ~10, both hardcoded
 * upstream). We render no labels, so that jog is noise. Dropping waypoints that
 * sit within `tolerance` of the line between their neighbours removes it while
 * keeping real detours, which are node-width sized and far larger.
 */
function straighten(points: Point[], tolerance = 14): Point[] {
  if (points.length < 3) return points;
  const kept = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = kept[kept.length - 1];
    const current = points[i];
    const next = points[i + 1];
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const span = Math.hypot(dx, dy);
    const deviation = span
      ? Math.abs((current.x - prev.x) * dy - (current.y - prev.y) * dx) / span
      : 0;
    if (deviation <= tolerance) continue;
    // A waypoint the path doubles back on renders as a hook; the detour is never real.
    const inX = current.x - prev.x;
    const inY = current.y - prev.y;
    const outX = next.x - current.x;
    const outY = next.y - current.y;
    const inLength = Math.hypot(inX, inY);
    const outLength = Math.hypot(outX, outY);
    if (inLength && outLength) {
      const cosine = (inX * outX + inY * outY) / (inLength * outLength);
      if (cosine < -0.5) continue;
    }
    kept.push(current);
  }
  kept.push(points[points.length - 1]);
  return kept;
}

/** Straight segments joined by rounded corners, rather than a swooping spline. */
function roundedPath(rawPoints: Point[]): string {
  const points = straighten(rawPoints);
  if (points.length < 2) return '';
  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const corner = points[i];
    const before = towards(corner, points[i - 1], CORNER_RADIUS);
    const after = towards(corner, points[i + 1], CORNER_RADIUS);
    d += ` L${before.x},${before.y} Q${corner.x},${corner.y} ${after.x},${after.y}`;
  }
  const last = points[points.length - 1];
  return `${d} L${last.x},${last.y}`;
}

/**
 * Arrowheads can't inherit their line's stroke, so each colour needs its own
 * marker. Markers resolve by document-wide id, so this can live outside the graph svg.
 */
export const CatalogGraphEdgeDefs = () => {
  const isDark = useTheme().palette.type === 'dark';
  const colors = getDependencyColors(isDark);
  const relColor = relationColor(isDark);
  return (
    <svg
      width={0}
      height={0}
      aria-hidden
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
      <defs>
        {[...TIERS, 'relation'].map(tier => (
          <marker
            key={tier}
            id={`${ARROW_PREFIX}-${tier}`}
            viewBox="0 -5 10 10"
            refX={9}
            refY={0}
            markerWidth={12}
            markerHeight={12}
            markerUnits="userSpaceOnUse"
            orient="auto"
          >
            <path
              d="M0,-4L10,0L0,4"
              fill={tier === 'relation' ? relColor : colors[tier]}
            />
          </marker>
        ))}
      </defs>
    </svg>
  );
};

/** Midpoint of the middle segment, used to anchor the edge label. */
function midpoint(points: Point[]): Point {
  if (points.length === 1) return points[0];
  const index = Math.max(0, Math.floor(points.length / 2) - 1);
  const a = points[index];
  const b = points[index + 1] ?? a;
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

interface CatalogGraphEdgeProps {
  edge: { points?: Point[]; relations?: string[] };
  grade?: DependencyGrade;
  showLabel?: boolean;
}

export const CatalogGraphEdge = ({ edge, grade, showLabel }: CatalogGraphEdgeProps) => {
  const isDark = useTheme().palette.type === 'dark';
  const colors = getDependencyColors(isDark);
  const points = (edge.points ?? []).filter(p => isFinite(p.x) && isFinite(p.y));
  const d = roundedPath(points);
  if (!d) return null;

  const tier = grade ? dependencyTier(grade) : 'relation';
  const stroke = tier === 'relation' ? relationColor(isDark) : colors[tier];
  const relations = edge.relations ?? [];
  const relationText = relations.join(' / ');
  const criticalityText = grade ? dependencyLabel(grade) : '';
  const anchor = showLabel && relationText ? midpoint(points) : undefined;
  const labelFill = isDark ? '#c9d1d9' : '#57606a';
  const labelAltFill = isDark ? '#8b949e' : '#57606a';
  const halo = isDark ? HALO_DARK : HALO_LIGHT;

  return (
    <g>
      <title>
        {[relationText, criticalityText].filter(Boolean).join(' — ')}
      </title>
      <path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={EDGE_WIDTHS[tier]}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={tier !== 'critical' && tier !== 'relation' ? '5 4' : undefined}
        markerEnd={`url(#${ARROW_PREFIX}-${tier})`}
      />
      {anchor && (
        <g transform={`translate(${anchor.x}, ${anchor.y})`} pointerEvents="none">
          <text
            textAnchor="middle"
            y={criticalityText ? -2 : 3}
            fontSize={9}
            fontFamily="sans-serif"
            fill={labelFill}
            style={halo}
          >
            {relations.map((relation, index) => (
              <tspan key={relation} fill={index % 2 ? labelAltFill : undefined}>
                {index > 0 ? ' / ' : ''}
                {relation}
              </tspan>
            ))}
          </text>
          {criticalityText && (
            <text
              textAnchor="middle"
              y={10}
              fontSize={9}
              fontWeight={600}
              fontFamily="sans-serif"
              fill={stroke}
              style={halo}
            >
              {criticalityText}
            </text>
          )}
        </g>
      )}
    </g>
  );
};

const EDGE_LEGEND: Array<{ tier: string; label: string }> = [
  { tier: 'critical', label: 'Critical' },
  { tier: 'high', label: 'Non-critical (high)' },
  { tier: 'medium', label: 'Non-critical (medium)' },
  { tier: 'low', label: 'Non-critical (low)' },
  { tier: 'unknown', label: 'Unknown' },
  { tier: 'relation', label: 'Structural / API' },
];

const DEFAULT_LEGEND_KINDS = ['Domain', 'System', 'Component', 'Resource', 'API'];

const headingStyle = {
  color: '#c9d1d9',
  fontSize: 9,
  fontWeight: 600,
  marginBottom: 3,
} as const;

const labelStyle = { color: '#8b949e', fontSize: 10 } as const;

/**
 * Builds the renderEdge callback, colouring dependency edges by the criticality
 * the system-registry provider recorded on the source entity.
 */
export function useDependencyEdgeRenderer(showLabels = true) {
  const catalogApi = useApi(catalogApiRef);
  // Keyed `${sourceRef}|${targetRef}`, both lowercased.
  const [grades, setGrades] = useState<Record<string, DependencyGrade>>({});

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: ['System', 'Component', 'Resource', 'API'] },
        fields: ['kind', 'metadata.name', 'metadata.namespace', 'metadata.annotations'],
      })
      .then(res => {
        if (cancelled) return;
        const map: Record<string, DependencyGrade> = {};
        for (const entity of res.items) {
          const source = stringifyEntityRef(entity).toLowerCase();
          for (const [target, grade] of Object.entries(readDependencyGrades(entity))) {
            map[`${source}|${target.toLowerCase()}`] = grade;
          }
        }
        setGrades(map);
      })
      .catch(() => setGrades({}));
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  return useCallback(
    ({ edge, id }: any) => {
      const relations: string[] = edge.relations ?? [];
      const from = (id?.v ?? edge.from ?? '').toLowerCase();
      const to = (id?.w ?? edge.to ?? '').toLowerCase();
      let grade: DependencyGrade | undefined;
      if (relations.includes(RELATION_DEPENDS_ON)) {
        grade = grades[`${from}|${to}`];
      } else if (relations.includes(RELATION_DEPENDENCY_OF)) {
        grade = grades[`${to}|${from}`];
      }
      return <CatalogGraphEdge edge={edge} grade={grade} showLabel={showLabels} />;
    },
    [grades, showLabels],
  );
}

/** Card header control for turning the edge text labels on and off. */
export const EdgeLabelToggle = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
}) => (
  <FormControlLabel
    control={
      <Switch
        size="small"
        color="primary"
        checked={checked}
        onChange={event => onChange(event.target.checked)}
      />
    }
    label={<Typography style={{ fontSize: 12, color: '#8b949e' }}>Labels</Typography>}
  />
);

export const CatalogGraphLegend = ({
  kinds = DEFAULT_LEGEND_KINDS,
}: {
  kinds?: string[];
}) => (
  <Box
    style={{
      position: 'absolute',
      // Below the card title, so it stays on screen without scrolling the card.
      top: 56,
      left: 16,
      zIndex: 5,
      background: 'rgba(13,17,23,0.92)',
      border: '1px solid #30363d',
      borderRadius: 6,
      padding: '8px 12px',
      display: 'flex',
      gap: 18,
      pointerEvents: 'none',
    }}
  >
    <Box>
      <Typography style={headingStyle}>Nodes</Typography>
      {kinds.map(kind => {
        const palette = kindPalette(kind);
        return (
          <Box key={kind} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <Box
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: palette.fill,
                border: `1px solid ${palette.stroke}`,
              }}
            />
            <Typography style={labelStyle}>{kind}</Typography>
          </Box>
        );
      })}
    </Box>
    <Box>
      <Typography style={headingStyle}>Edges</Typography>
      {EDGE_LEGEND.map(({ tier, label }) => (
        <Box key={tier} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
          <Box
            style={{
              width: 22,
              height: 0,
              // This panel is always dark internally regardless of app theme, so its
              // edge previews always use the dark-mode colours to match.
              borderTop: `${EDGE_WIDTHS[tier]}px ${
                tier === 'critical' || tier === 'relation' ? 'solid' : 'dashed'
              } ${tier === 'relation' ? relationColor(true) : getDependencyColors(true)[tier]}`,
            }}
          />
          <Typography style={labelStyle}>{label}</Typography>
        </Box>
      ))}
    </Box>
  </Box>
);
